// services/moduleService.ts
import axios from "axios";
import { Module, BaseModule, EntrepriseModule, ModuleConfig } from "../types/features/Module";
import { PaginatedResponse } from "../types/core/PaginatedResponse";
import { apiClient } from "../utils/apiClient";

export const moduleService = {
    // ============================================
    // ROUTES PUBLIQUES
    // ============================================

    // Liste paginée des modules (public)
    async getModules(
        page: number = 1,
        perPage: number = 20,
        search?: string,
        is_active?: boolean
    ): Promise<PaginatedResponse<Module>> {
        const response = await apiClient.get<PaginatedResponse<Module>>('/modules', {
            params: {
                page,
                per_page: perPage,
                search,
                is_active
            },
        });

        return response.data;
    },

    // Détails d'un module (public)
    async getModuleById(id: string): Promise<Module> {
        const response = await apiClient.get<{ statusCode: number; data: Module }>(`/modules/${id}`);
        return response.data.data;
    },

    // ============================================
    // ROUTES ENTREPRISE (Modules de l'entreprise connectée)
    // ============================================

    // Récupérer les modules de l'entreprise connectée
    async getEntrepriseModules(): Promise<{ data: EntrepriseModule[]; entreprise: { id: string; name: string } }> {
        const response = await apiClient.get<{ statusCode: number; data: EntrepriseModule[]; entreprise: { id: string; name: string } }>('/modules/entreprise');
        return response.data;
    },

    // Activer un module pour l'entreprise
    async activateModule(moduleId: string): Promise<{ module_id: string; is_active: boolean; activated_at: string }> {
        const response = await apiClient.post<{ statusCode: number; message: string; data: { module_id: string; is_active: boolean; activated_at: string } }>(`/modules/${moduleId}/activate`);
        return response.data.data;
    },

    // Désactiver un module pour l'entreprise
    async deactivateModule(moduleId: string): Promise<{ module_id: string; is_active: boolean }> {
        const response = await apiClient.post<{ statusCode: number; message: string; data: { module_id: string; is_active: boolean } }>(`/modules/${moduleId}/deactivate`);
        return response.data.data;
    },

    // Basculer l'état d'un module (actif/inactif)
    async toggleModule(moduleId: string): Promise<{ module_id: string; is_active: boolean; activated_at: string | null }> {
        const response = await apiClient.post<{ statusCode: number; message: string; data: { module_id: string; is_active: boolean; activated_at: string | null } }>(`/modules/${moduleId}/toggle`);
        return response.data.data;
    },

    // ============================================
    // ROUTES ADMIN
    // ============================================

    // Créer un module (admin)
    async createModule(module: Omit<Module, "id" | "created_at" | "updated_at">): Promise<Module> {
        const response = await apiClient.post<{ statusCode: number; message: string; data: Module }>('/modules', module);
        return response.data.data;
    },

    // Mettre à jour un module (admin)
    async updateModule(id: string, data: Partial<BaseModule>): Promise<Module> {
        const response = await apiClient.put<{ statusCode: number; message: string; data: Module }>(`/modules/${id}`, data);
        return response.data.data;
    },

    // Supprimer un module (admin)
    async deleteModule(id: string): Promise<void> {
        await apiClient.delete(`/modules/${id}`);
    },

    // Activer/Désactiver un module globalement (admin)
    async toggleStatus(id: string): Promise<Module> {
        const response = await apiClient.patch<{ statusCode: number; message: string; data: Module }>(`/modules/${id}/toggle`);
        return response.data.data;
    },

    // ============================================
    // ROUTES MODULE CONFIGS (NOUVEAU)
    // ============================================

    /**
     * Récupère toutes les configurations d'un module
     */
    async getModuleConfigs(moduleName: string): Promise<{ module: string; is_active: boolean; configs: Record<string, string> }> {
        const response = await apiClient.get<{ module: string; is_active: boolean; configs: Record<string, string>}>(
            `/modules/${moduleName}/configs`
        );

        return response.data;
    },

    /**
     * Récupère une configuration spécifique
     */
    async getModuleConfig(moduleName: string, key: string): Promise<{ key: string; value: string; module: string }> {
        const response = await apiClient.get<{ statusCode: number; data: { key: string; value: string; module: string } }>(
            `/modules/${moduleName}/configs/${key}`
        );

        return response.data.data;
    },

    /**
     * Crée ou met à jour une configuration
     */
    async setModuleConfig(moduleName: string, key: string, value: string): Promise<{ key: string; value: string }> {
        const response = await apiClient.put<{ statusCode: number; message: string; key: string; value: string }>(
            `/modules/${moduleName}/configs/${key}`,
            { value }
        );
        return { key: response.data.key, value: response.data.value };
    },

    /**
     * Supprime une configuration
     */
    async deleteModuleConfig(moduleName: string, key: string): Promise<{ message: string }> {
        const response = await apiClient.delete<{ statusCode: number; message: string }>(
            `/modules/${moduleName}/configs/${key}`
        );
        return { message: response.data.message };
    },

    /**
     * Met à jour plusieurs configurations en une seule requête
     */
    async batchModuleConfigs(moduleName: string, configs: Array<{ key: string; value: string }>): Promise<{ updated: string[]; created: string[] }> {
        const response = await apiClient.post<{ statusCode: number; message: string; updated: string[]; created: string[] }>(
            `/modules/${moduleName}/configs/batch`,
            { configs }
        );
        return { updated: response.data.updated, created: response.data.created };
    },
};